import {arrayIntersected} from "helpers/array";
import {TContinent, TCountry, TRegion, TState} from "../types";
import {getCountryIdsFromContinent} from "./idHelpers";

export type TExpandedState = Record<number, boolean>;

/**
 * Recursively collect expandable map entries for search highlighting/expansion.
 * If a descendant matches, mark ancestors expanded.
 */
export const getLocationExpandableData = (continents: TContinent[] = []): TExpandedState => {
	const expandable: TExpandedState = {};

	const mark = (id?: number) => {
		if (id !== undefined) expandable[id] = true;
	};

	for (const cont of continents) {
		if (!cont.countries) continue;

		for (const country of cont.countries) {
			mark(country.id);
			mark(cont.id);

			if (!country.regions) continue;

			for (const region of country.regions) {
				mark(region.id);
				mark(country.id);
				mark(cont.id);

				if (!region.states) continue;

				for (const state of region.states) {
					mark(state.id);
					mark(region.id);
					mark(country.id);
					mark(cont.id);

					if (!state.cities) continue;

					for (const city of state.cities) {
						mark(city.id);
						mark(state.id);
						mark(region.id);
						mark(country.id);
						mark(cont.id);
					}
				}
			}
		}
	}

	return expandable;
};

/**
 * Check if a continent has any selected countries
 */
export const hasContinentSelectedHelper = (
	continent: TContinent,
	selectedLocation: number[],
): boolean => arrayIntersected(selectedLocation, getCountryIdsFromContinent(continent));

/**
 * Check if a continent has ALL countries selected
 */
export const isContinentFullySelectedHelper = (
	continent: TContinent,
	selectedLocation: number[],
): boolean => {
	const countryIds = getCountryIdsFromContinent(continent);

	if (countryIds.length === 0) return false;

	return countryIds.every((id) => selectedLocation.includes(id));
};

/**
 * Recursively collapse all descendants of a node
 */
export const collapseDescendants = (
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	node: any,
	currentExpanded: TExpandedState,
): TExpandedState => {
	const toUpdate = {...currentExpanded, [node.id]: false};

	// If node has countries (continent), collapse all deeper
	if (node?.countries) {
		for (const country of node.countries) {
			toUpdate[country.id] = false;
			if (country.regions) {
				for (const region of country.regions) {
					toUpdate[region.id] = false;
					if (region.states) {
						for (const state of region.states) {
							toUpdate[state.id] = false;
							if (state.cities) {
								for (const city of state.cities) {
									toUpdate[city.id] = false;
								}
							}
						}
					}
				}
			}
		}
	}

	// If node has regions (country)
	if (node?.regions) {
		for (const region of node.regions) {
			toUpdate[region.id] = false;
			if (region.states) {
				for (const state of region.states) {
					toUpdate[state.id] = false;
					if (state.cities) {
						for (const city of state.cities) {
							toUpdate[city.id] = false;
						}
					}
				}
			}
		}
	}

	// If node has states (region)
	if (node?.states) {
		for (const state of node.states) {
			toUpdate[state.id] = false;
			if (state.cities) {
				for (const city of state.cities) {
					toUpdate[city.id] = false;
				}
			}
		}
	}

	// If node has cities (state)
	if (node?.cities) {
		for (const city of node.cities) {
			toUpdate[city.id] = false;
		}
	}

	return toUpdate;
};
/**
 * Find full data objects from the source data
 */
export const getFullDataObjects = (
	country: TCountry,
	region: TRegion,
	state: TState,
	flattenCountries: TCountry[],
): {fullCountry: TCountry; fullRegion: TRegion; fullState: TState} => {
	const fullCountry =
		flattenCountries.find((c) => Number(c.id) === Number(country.id)) || country;

	let fullRegion = region;
	if (Number(region.id) !== 0 && fullCountry.regions) {
		fullRegion = fullCountry.regions.find((r) => Number(r.id) === Number(region.id)) || region;
	}

	let fullState = state;
	if (Number(state.id) !== 0) {
		if (fullRegion.states) {
			fullState = fullRegion.states.find((s) => Number(s.id) === Number(state.id)) || state;
		} else if (fullCountry.states) {
			fullState = fullCountry.states.find((s) => Number(s.id) === Number(state.id)) || state;
		}
	}

	return {fullCountry, fullRegion, fullState};
};
